/**
 * Klapzo public marketing landing page.
 * Single-file, trilingual, brand-compliant (BRAND.md v1.0.0 / Motora 2019).
 *
 * Brand discipline checklist (must hold at every review):
 *   - 60/30/10: neutral dominant, yellow+black constant, ONE accent (blue) per surface.
 *   - Yellow/green/coral = fills only with black text. Never as text on light bg.
 *   - Blue = only chromatic text color allowed (`text-accent`).
 *   - font-display (Montserrat ExtraBold) for headlines only.
 *   - font-body (Merriweather) for everything else.
 *   - rounded-none or rounded-md. rounded-full only for pill badges.
 *   - No shadows except elevated cards/modals (sparingly).
 *   - No gradients between brand colors.
 *   - No emoji in headlines.
 *   - No fabricated testimonials, client logos, or concrete Pro prices.
 */

import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { normalizeJoinCode } from "../api.js";

// ─── Types ────────────────────────────────────────────────────────────────────

export type Lang = "pt-BR" | "en-US" | "es-LATAM";

// ─── Copy ─────────────────────────────────────────────────────────────────────

type NavCopy = {
  recursos: string;
  precos: string;
  kit: string;
  comparar: string;
  entrar: string;
  langLabel: string;
};

type HeroCopy = {
  headline: string;
  subheadline: string;
  cta: string;
  ctaSecondary: string;
  badge: string;
  /** The participant's way in (W10a, US-LV-17 AC2) — the only functional control on this page. */
  join: { label: string; placeholder: string; cta: string; invalid: string };
};

type ValueCopy = {
  headline: string;
  body: string;
  comparisonLabel: string;
  comparisonItems: ReadonlyArray<{
    them: string;
    us: string;
  }>;
};

type LiveFeatureCopy = {
  headline: string;
  subhead: string;
  formats: ReadonlyArray<{
    title: string;
    description: string;
  }>;
  participantNote: string;
};

type KitFeatureCopy = {
  headline: string;
  subhead: string;
  features: ReadonlyArray<{
    title: string;
    description: string;
  }>;
};

type HowItWorksCopy = {
  headline: string;
  steps: ReadonlyArray<{
    number: string;
    title: string;
    description: string;
  }>;
};

type PricingCopy = {
  headline: string;
  subhead: string;
  plans: ReadonlyArray<{
    name: string;
    price: string;
    priceSub: string;
    features: ReadonlyArray<string>;
    cta: string;
    highlight: boolean;
  }>;
  disclaimer: string;
};

type FaqCopy = {
  headline: string;
  items: ReadonlyArray<{ question: string; answer: string }>;
};

type FinalCtaCopy = {
  headline: string;
  body: string;
  cta: string;
};

type FooterCopy = {
  made: string;
  legal: string;
  privacy: string;
  links: ReadonlyArray<{ label: string; href: string }>;
};

type CopyShape = {
  nav: NavCopy;
  hero: HeroCopy;
  value: ValueCopy;
  live: LiveFeatureCopy;
  kit: KitFeatureCopy;
  howItWorks: HowItWorksCopy;
  pricing: PricingCopy;
  faq: FaqCopy;
  finalCta: FinalCtaCopy;
  footer: FooterCopy;
};

/** Exported so `marketing-pricing.test.ts` can assert the published prices and limits match
 *  what the server enforces — the landing page IS the public price list. */
export const COPY: Record<Lang, CopyShape> = {
  "pt-BR": {
    nav: {
      recursos: "Recursos",
      precos: "Preços",
      kit: "Kit de Facilitação",
      comparar: "Comparar",
      entrar: "Entrar",
      langLabel: "PT",
    },
    hero: {
      headline: "Sessões ao vivo que prendem a atenção do começo ao fim",
      subheadline:
        "Klapzo combina engajamento em tempo real e planejamento de agenda numa plataforma só, em português, com preço em Real. O jeito brasileiro de facilitar com excelência.",
      cta: "Começar gratuitamente",
      ctaSecondary: "Ver recursos",
      badge: "Feito no Brasil — LGPD por padrão",
      join: {
        label: "Vai participar? Digite o código da sala:",
        placeholder: "ABC234",
        cta: "Entrar",
        invalid: "Código inválido. São 6 caracteres, sem 0, O, 1, I ou L.",
      },
    },
    value: {
      headline: "Substitua três ferramentas caras por uma solução brasileira",
      body: "Mentimeter + Kahoot + SessionLab juntos custam entre R$ 300 e R$ 500 por mês, todos em inglês, com suporte fora do seu fuso horário. Klapzo reúne engajamento ao vivo e planejamento de agenda numa única plataforma — em português, no seu preço, com LGPD nativa.",
      comparisonLabel: "O que você deixa de pagar:",
      comparisonItems: [
        {
          them: "Mentimeter — enquetes e nuvem de palavras",
          us: "Klapzo Live — mesmo recurso, em PT-BR",
        },
        {
          them: "Kahoot — quiz gamificado",
          us: "Klapzo Live — quiz pontuado com ranking em tempo real",
        },
        {
          them: "SessionLab — planejador de agenda",
          us: "Klapzo Kit — biblioteca de métodos + planejador com arrastar-e-soltar",
        },
      ],
    },
    live: {
      headline: "Klapzo Live",
      subhead:
        "Cinco formatos de engajamento em tempo real para suas sessões, aulas e eventos. Participantes entram pelo celular — sem baixar nada.",
      formats: [
        {
          title: "Enquete",
          description:
            "Resposta rápida com resultado ao vivo. Ideal para tomadas de decisão e verificação de entendimento.",
        },
        {
          title: "Quiz com ranking",
          description:
            "Quiz pontuado com leaderboard em tempo real. Estimula competição saudável e aprendizado ativo.",
        },
        {
          title: "Nuvem de palavras",
          description:
            "Respostas dos participantes viram uma nuvem visual dinâmica. Perfeita para aquecimento e brainstorming.",
        },
        {
          title: "Perguntas abertas",
          description:
            "Participantes enviam perguntas e curtem as dos outros. As mais votadas sobem automaticamente.",
        },
        {
          title: "Projeção em tela cheia",
          description:
            "Resultados ao vivo projetados no telão, prontos para qualquer apresentação presencial ou híbrida.",
        },
      ],
      participantNote:
        "Acesso por código ou QR code — sem cadastro, sem app, só um apelido.",
    },
    kit: {
      headline: "Klapzo Kit",
      subhead:
        "Biblioteca de métodos de facilitação em português e planejador de agenda inteligente para que você chegue em cada sessão com tudo preparado.",
      features: [
        {
          title: "Biblioteca de métodos",
          description:
            "Centenas de dinâmicas, icebreakers e técnicas de facilitação catalogados em português. Filtre por objetivo, tempo e energia.",
        },
        {
          title: "Planejador de agenda",
          description:
            "Arraste e solte atividades para montar sua agenda. O sistema calcula os tempos automaticamente e avisa quando você estourou.",
        },
        {
          title: "IA que ajuda, você decide",
          description:
            "Sugestões de rascunho geradas por IA para quiz e atividades — sempre com revisão humana antes de publicar. A IA propõe; você aprova.",
        },
      ],
    },
    howItWorks: {
      headline: "Como funciona",
      steps: [
        {
          number: "01",
          title: "Crie sua sessão",
          description:
            "Adicione os formatos que quiser — enquete, quiz, nuvem de palavras — e monte a agenda no Kit.",
        },
        {
          number: "02",
          title: "Compartilhe o código",
          description:
            "Participantes entram pelo link, QR code ou código de 6 dígitos. Sem app, sem cadastro.",
        },
        {
          number: "03",
          title: "Facilite ao vivo",
          description:
            "Projete os resultados em tempo real no telão. Exporte o relatório ao final.",
        },
      ],
    },
    pricing: {
      headline: "Planos",
      subhead:
        "Comece gratuitamente. Cresça no seu ritmo — sem surpresa na fatura.",
      plans: [
        {
          name: "Grátis",
          price: "R$ 0",
          priceSub: "para sempre",
          features: [
            "30 participantes por sala",
            "3 rodadas por sala · 1 sala por vez",
            "Todos os formatos de engajamento",
            "Klapzo Kit (biblioteca + planejador)",
            "Histórico de 30 dias · sem exportação",
          ],
          cta: "Começar agora",
          highlight: false,
        },
        {
          name: "Facilitador",
          price: "R$ 59",
          priceSub: "por mês, no plano anual (R$ 79 mensal)",
          features: [
            "100 participantes por sala",
            "Rodadas ilimitadas",
            "Exportação CSV/JSON e relatório para impressão",
            "Histórico de 365 dias",
            "Klapzo Kit completo",
          ],
          cta: "Assinar",
          highlight: true,
        },
        {
          name: "Equipe",
          price: "R$ 189",
          priceSub: "por mês, no plano anual — 5 assentos",
          features: [
            "200 participantes por sala",
            "5 salas simultâneas",
            "5 assentos de facilitador",
            "Histórico de 730 dias",
            "Tudo do Facilitador",
          ],
          cta: "Assinar",
          highlight: false,
        },
        {
          name: "Corporativo",
          price: "a partir de R$ 890",
          priceSub: "por mês, com contrato",
          features: [
            "1.000 participantes por sala",
            "25 assentos · 25 salas simultâneas",
            "Retenção indefinida",
            "SSO e marca própria",
            "Onboarding e suporte dedicados",
          ],
          cta: "Falar com vendas",
          highlight: false,
        },
      ],
      disclaimer:
        "Precisa de um evento único? O passe Evento custa R$ 349 por 7 dias, com 300 participantes por sala e sem assinatura. Preços em reais, pagáveis por PIX, boleto ou cartão — sem IOF e sem variação cambial.",
    },
    faq: {
      headline: "Perguntas frequentes",
      items: [
        {
          question: "Os participantes precisam criar conta?",
          answer:
            "Não. Participantes entram apenas com um apelido escolhido na hora — sem e-mail, sem cadastro, sem app instalado. Os dados são anônimos e efêmeros por padrão (LGPD).",
        },
        {
          question: "Klapzo funciona em eventos presenciais e remotos?",
          answer:
            "Sim. Funciona em qualquer formato: 100% presencial (participantes no celular, resultado no telão), híbrido ou 100% remoto. A projeção em tela cheia foi pensada especialmente para auditórios.",
        },
        {
          question: "Qual a diferença entre Klapzo Live e Klapzo Kit?",
          answer:
            "Klapzo Live são os formatos de engajamento em tempo real (enquetes, quiz, nuvem de palavras etc.). Klapzo Kit é a biblioteca de métodos de facilitação e o planejador de agenda. Os dois ficam no mesmo plano — você usa os dois juntos ou separados.",
        },
        {
          question: "A IA publica conteúdo automaticamente?",
          answer:
            "Nunca. A IA gera apenas rascunhos — um ponto de partida para quiz e atividades. Você revisa, edita e publica. A publicação é sempre uma ação humana e intencional.",
        },
        {
          question: "Klapzo é seguro para dados de participantes?",
          answer:
            "Sim. Klapzo foi projetado com LGPD como requisito de arquitetura, não como adendo. Participantes são anônimos por padrão, sem coleta de PII. Para organizadores, os dados ficam em infraestrutura com conformidade brasileira.",
        },
        {
          question: "Klapzo funciona bem em conexões lentas?",
          answer:
            "Sim. Klapzo roda na borda (Cloudflare Workers), o que significa latência mínima em todo o Brasil. O app de participante é leve e funciona em redes 3G.",
        },
      ],
    },
    finalCta: {
      headline: "Sua próxima sessão começa aqui",
      body: "Junte-se aos facilitadores que já escolheram uma alternativa brasileira, em português, com preço em Real.",
      cta: "Criar minha conta grátis",
    },
    footer: {
      made: "Feito com cuidado no Brasil por The Labs Group · SkilLab",
      legal: `© ${new Date().getFullYear()} SkilLab. Todos os direitos reservados.`,
      privacy: "Privacidade",
      links: [
        { label: "Recursos", href: "#recursos" },
        { label: "Preços", href: "#precos" },
        { label: "Kit de Facilitação", href: "/kit" },
        { label: "Privacidade", href: "/privacidade" },
        { label: "Termos", href: "/termos" },
        { label: "Contato", href: "mailto:contato@klapzo.app" },
      ],
    },
  },

  "en-US": {
    nav: {
      recursos: "Features",
      precos: "Pricing",
      kit: "Facilitation Kit",
      comparar: "Compare",
      entrar: "Sign in",
      langLabel: "EN",
    },
    hero: {
      headline: "Live sessions that keep every participant engaged",
      subheadline:
        "Klapzo combines real-time audience engagement and session planning in one platform — built in Brazil, priced in Brazilian Reais, LGPD-compliant by design.",
      cta: "Start for free",
      ctaSecondary: "Explore features",
      badge: "Made in Brazil — privacy by default",
      join: {
        label: "Joining a session? Enter the room code:",
        placeholder: "ABC234",
        cta: "Join",
        invalid: "Invalid code. Six characters, with no 0, O, 1, I or L.",
      },
    },
    value: {
      headline: "Replace three expensive tools with one Brazilian solution",
      body: "Mentimeter + Kahoot + SessionLab together cost between R$ 300 and R$ 500 per month, all in English, with support outside your time zone. Klapzo brings live engagement and session planning into a single platform — in Portuguese, at a local price, with LGPD built in.",
      comparisonLabel: "What you stop paying for:",
      comparisonItems: [
        {
          them: "Mentimeter — polls and word clouds",
          us: "Klapzo Live — same capability, in PT-BR",
        },
        {
          them: "Kahoot — gamified quizzes",
          us: "Klapzo Live — scored quizzes with real-time leaderboard",
        },
        {
          them: "SessionLab — agenda planner",
          us: "Klapzo Kit — method library + drag-and-drop planner",
        },
      ],
    },
    live: {
      headline: "Klapzo Live",
      subhead:
        "Five real-time engagement formats for your sessions, classes, and events. Participants join on their phones — no download required.",
      formats: [
        {
          title: "Poll",
          description:
            "Fast multiple-choice responses with live results. Perfect for quick decisions and comprehension checks.",
        },
        {
          title: "Quiz with leaderboard",
          description:
            "Scored quiz with a real-time ranking board. Drives healthy competition and active learning.",
        },
        {
          title: "Word cloud",
          description:
            "Participant responses form a live visual word cloud. Great for warm-ups and open brainstorming.",
        },
        {
          title: "Open Q&A",
          description:
            "Participants submit questions and upvote others. The most popular questions rise automatically.",
        },
        {
          title: "Fullscreen projection",
          description:
            "Live results projected on the big screen, ready for any in-person or hybrid event.",
        },
      ],
      participantNote:
        "Join by code or QR — no sign-up, no app, just a nickname.",
    },
    kit: {
      headline: "Klapzo Kit",
      subhead:
        "A facilitation method library in Portuguese and a smart agenda planner so you arrive at every session fully prepared.",
      features: [
        {
          title: "Method library",
          description:
            "Hundreds of dynamics, icebreakers, and facilitation techniques catalogued in Portuguese. Filter by goal, time, and energy level.",
        },
        {
          title: "Agenda planner",
          description:
            "Drag and drop activities to build your agenda. The system calculates run times automatically and warns you when you're over.",
        },
        {
          title: "AI assists, you decide",
          description:
            "AI-generated draft suggestions for quizzes and activities — always with human review before publishing. The AI proposes; you approve.",
        },
      ],
    },
    howItWorks: {
      headline: "How it works",
      steps: [
        {
          number: "01",
          title: "Create your session",
          description:
            "Add the engagement formats you need — poll, quiz, word cloud — and plan your agenda in the Kit.",
        },
        {
          number: "02",
          title: "Share the code",
          description:
            "Participants join via link, QR code, or a 6-digit code. No app, no registration.",
        },
        {
          number: "03",
          title: "Facilitate live",
          description:
            "Project real-time results on the big screen. Export the full report at the end.",
        },
      ],
    },
    pricing: {
      headline: "Pricing",
      subhead: "Start free. Grow at your own pace — no invoice surprises.",
      plans: [
        {
          name: "Free",
          price: "R$ 0",
          priceSub: "forever",
          features: [
            "30 participants per room",
            "3 rounds per room · 1 room at a time",
            "All engagement formats",
            "Klapzo Kit (library + planner)",
            "30-day history · no export",
          ],
          cta: "Start now",
          highlight: false,
        },
        {
          name: "Facilitator",
          price: "R$ 59",
          priceSub: "per month, billed annually (R$ 79 monthly)",
          features: [
            "100 participants per room",
            "Unlimited rounds",
            "CSV/JSON export and printable report",
            "365-day history",
            "The full Klapzo Kit",
          ],
          cta: "Subscribe",
          highlight: true,
        },
        {
          name: "Team",
          price: "R$ 189",
          priceSub: "per month, billed annually — 5 seats",
          features: [
            "200 participants per room",
            "5 concurrent rooms",
            "5 facilitator seats",
            "730-day history",
            "Everything in Facilitator",
          ],
          cta: "Subscribe",
          highlight: false,
        },
        {
          name: "Corporate",
          price: "from R$ 890",
          priceSub: "per month, on contract",
          features: [
            "1,000 participants per room",
            "25 seats · 25 concurrent rooms",
            "Indefinite retention",
            "SSO and white-label",
            "Dedicated onboarding and support",
          ],
          cta: "Talk to sales",
          highlight: false,
        },
      ],
      disclaimer:
        "Running a one-off event? The Event pass is R$ 349 for 7 days, with 300 participants per room and no subscription. Prices in Brazilian reais, payable by PIX, boleto or card — no IOF and no FX drift.",
    },
    faq: {
      headline: "Frequently asked questions",
      items: [
        {
          question: "Do participants need to create an account?",
          answer:
            "No. Participants join with a nickname chosen on the spot — no email, no sign-up, no app install. Data is anonymous and ephemeral by default (LGPD-compliant).",
        },
        {
          question: "Does Klapzo work for in-person and remote events?",
          answer:
            "Yes. It works in any format: 100% in-person (participants on phones, results on the big screen), hybrid, or fully remote. Fullscreen projection was designed specifically for auditoriums.",
        },
        {
          question: "What's the difference between Klapzo Live and Klapzo Kit?",
          answer:
            "Klapzo Live covers the real-time engagement formats (polls, quiz, word cloud, etc.). Klapzo Kit is the facilitation method library and agenda planner. Both are included in the same plan — use them together or separately.",
        },
        {
          question: "Does the AI publish content automatically?",
          answer:
            "Never. The AI only generates drafts — a starting point for quizzes and activities. You review, edit, and publish. Publishing is always a deliberate human action.",
        },
        {
          question: "Is Klapzo safe for participant data?",
          answer:
            "Yes. Klapzo was architected with LGPD as a core requirement, not an afterthought. Participants are anonymous by default; no PII is collected. Organizer data is stored on Brazilian-compliant infrastructure.",
        },
        {
          question: "Does Klapzo work on slow connections?",
          answer:
            "Yes. Klapzo runs at the edge (Cloudflare Workers), which means minimal latency across all of Brazil. The participant app is lightweight and works on 3G networks.",
        },
      ],
    },
    finalCta: {
      headline: "Your next session starts here",
      body: "Join the facilitators who have already chosen a Brazilian alternative — in Portuguese, priced in Reais.",
      cta: "Create my free account",
    },
    footer: {
      made: "Made with care in Brazil by The Labs Group · SkilLab",
      legal: `© ${new Date().getFullYear()} SkilLab. All rights reserved.`,
      privacy: "Privacy",
      links: [
        { label: "Features", href: "#recursos" },
        { label: "Pricing", href: "#precos" },
        { label: "Facilitation Kit", href: "/kit" },
        { label: "Privacy", href: "/privacidade" },
        { label: "Terms", href: "/termos" },
        { label: "Contact", href: "mailto:contato@klapzo.app" },
      ],
    },
  },

  "es-LATAM": {
    nav: {
      recursos: "Funciones",
      precos: "Precios",
      kit: "Kit de Facilitación",
      comparar: "Comparar",
      entrar: "Ingresar",
      langLabel: "ES",
    },
    hero: {
      headline: "Sesiones en vivo que mantienen a todos participando",
      subheadline:
        "Klapzo combina interacción en tiempo real y planificación de agenda en una sola plataforma — hecho en Brasil, en portugués y español, con precios en moneda local y privacidad por diseño.",
      cta: "Empezar gratis",
      ctaSecondary: "Ver funciones",
      badge: "Hecho en Brasil — privacidad por defecto",
      join: {
        label: "¿Vas a participar? Escribe el código de la sala:",
        placeholder: "ABC234",
        cta: "Entrar",
        invalid: "Código inválido. Son 6 caracteres, sin 0, O, 1, I ni L.",
      },
    },
    value: {
      headline: "Reemplaza tres herramientas caras con una solución latinoamericana",
      body: "Mentimeter + Kahoot + SessionLab juntos cuestan entre R$ 300 y R$ 500 por mes, todos en inglés, con soporte fuera de tu zona horaria. Klapzo reúne interacción en vivo y planificación de agenda en una sola plataforma — en tu idioma, con precio local y LGPD nativa.",
      comparisonLabel: "Lo que dejas de pagar:",
      comparisonItems: [
        {
          them: "Mentimeter — encuestas y nubes de palabras",
          us: "Klapzo Live — misma función, en tu idioma",
        },
        {
          them: "Kahoot — cuestionarios gamificados",
          us: "Klapzo Live — quiz con puntaje y ranking en tiempo real",
        },
        {
          them: "SessionLab — planificador de agenda",
          us: "Klapzo Kit — biblioteca de métodos + planificador con arrastrar y soltar",
        },
      ],
    },
    live: {
      headline: "Klapzo Live",
      subhead:
        "Cinco formatos de interacción en tiempo real para tus sesiones, clases y eventos. Los participantes se unen desde el celular — sin instalar nada.",
      formats: [
        {
          title: "Encuesta",
          description:
            "Respuesta rápida con resultado en vivo. Ideal para tomas de decisión y verificación de comprensión.",
        },
        {
          title: "Quiz con ranking",
          description:
            "Quiz con puntaje y leaderboard en tiempo real. Estimula la competencia sana y el aprendizaje activo.",
        },
        {
          title: "Nube de palabras",
          description:
            "Las respuestas de los participantes forman una nube visual dinámica. Perfecta para calentamiento y brainstorming.",
        },
        {
          title: "Preguntas abiertas",
          description:
            "Los participantes envían preguntas y votan las de otros. Las más populares suben automáticamente.",
        },
        {
          title: "Proyección en pantalla completa",
          description:
            "Resultados en vivo proyectados en la pantalla grande, listos para cualquier evento presencial o híbrido.",
        },
      ],
      participantNote:
        "Ingreso por código o QR — sin registro, sin app, solo un apodo.",
    },
    kit: {
      headline: "Klapzo Kit",
      subhead:
        "Biblioteca de métodos de facilitación y planificador de agenda inteligente para que llegues a cada sesión con todo preparado.",
      features: [
        {
          title: "Biblioteca de métodos",
          description:
            "Cientos de dinámicas, rompehielos y técnicas de facilitación catalogadas en portugués. Filtra por objetivo, tiempo y nivel de energía.",
        },
        {
          title: "Planificador de agenda",
          description:
            "Arrastra y suelta actividades para armar tu agenda. El sistema calcula los tiempos automáticamente y te avisa cuando te pasas.",
        },
        {
          title: "IA que sugiere, tú decides",
          description:
            "Borradores generados por IA para quizzes y actividades — siempre con revisión humana antes de publicar. La IA propone; tú apruebas.",
        },
      ],
    },
    howItWorks: {
      headline: "Cómo funciona",
      steps: [
        {
          number: "01",
          title: "Crea tu sesión",
          description:
            "Agrega los formatos que quieras — encuesta, quiz, nube de palabras — y arma la agenda en el Kit.",
        },
        {
          number: "02",
          title: "Comparte el código",
          description:
            "Los participantes ingresan por enlace, código QR o código de 6 dígitos. Sin app, sin registro.",
        },
        {
          number: "03",
          title: "Facilita en vivo",
          description:
            "Proyecta los resultados en tiempo real en la pantalla grande. Exporta el informe al finalizar.",
        },
      ],
    },
    pricing: {
      headline: "Planes",
      subhead: "Empieza gratis. Crece a tu ritmo — sin sorpresas en la factura.",
      plans: [
        {
          name: "Gratis",
          price: "R$ 0",
          priceSub: "para siempre",
          features: [
            "30 participantes por sala",
            "3 rondas por sala · 1 sala a la vez",
            "Todos los formatos de participación",
            "Klapzo Kit (biblioteca + planificador)",
            "Historial de 30 días · sin exportación",
          ],
          cta: "Empezar ahora",
          highlight: false,
        },
        {
          name: "Facilitador",
          price: "R$ 59",
          priceSub: "por mes, en el plan anual (R$ 79 mensual)",
          features: [
            "100 participantes por sala",
            "Rondas ilimitadas",
            "Exportación CSV/JSON e informe para imprimir",
            "Historial de 365 días",
            "Klapzo Kit completo",
          ],
          cta: "Suscribirse",
          highlight: true,
        },
        {
          name: "Equipo",
          price: "R$ 189",
          priceSub: "por mes, en el plan anual — 5 asientos",
          features: [
            "200 participantes por sala",
            "5 salas simultáneas",
            "5 asientos de facilitador",
            "Historial de 730 días",
            "Todo lo de Facilitador",
          ],
          cta: "Suscribirse",
          highlight: false,
        },
        {
          name: "Corporativo",
          price: "desde R$ 890",
          priceSub: "por mes, con contrato",
          features: [
            "1.000 participantes por sala",
            "25 asientos · 25 salas simultáneas",
            "Retención indefinida",
            "SSO y marca propia",
            "Onboarding y soporte dedicados",
          ],
          cta: "Hablar con ventas",
          highlight: false,
        },
      ],
      disclaimer:
        "¿Un evento puntual? El pase Evento cuesta R$ 349 por 7 días, con 300 participantes por sala y sin suscripción. Precios en reales brasileños, pagaderos por PIX, boleto o tarjeta — sin IOF ni variación cambiaria.",
    },
    faq: {
      headline: "Preguntas frecuentes",
      items: [
        {
          question: "¿Los participantes necesitan crear una cuenta?",
          answer:
            "No. Los participantes ingresan solo con un apodo elegido en el momento — sin correo, sin registro, sin app. Los datos son anónimos y efímeros por defecto.",
        },
        {
          question: "¿Klapzo funciona para eventos presenciales y remotos?",
          answer:
            "Sí. Funciona en cualquier formato: 100% presencial (participantes en el celular, resultados en pantalla grande), híbrido o 100% remoto. La proyección en pantalla completa fue diseñada especialmente para auditorios.",
        },
        {
          question: "¿Cuál es la diferencia entre Klapzo Live y Klapzo Kit?",
          answer:
            "Klapzo Live son los formatos de interacción en tiempo real (encuestas, quiz, nube de palabras, etc.). Klapzo Kit es la biblioteca de métodos de facilitación y el planificador de agenda. Ambos están en el mismo plan — úsalos juntos o por separado.",
        },
        {
          question: "¿La IA publica contenido automáticamente?",
          answer:
            "Nunca. La IA solo genera borradores — un punto de partida para quizzes y actividades. Tú revisas, editas y publicas. La publicación siempre es una acción humana e intencional.",
        },
        {
          question: "¿Klapzo es seguro para los datos de participantes?",
          answer:
            "Sí. Klapzo fue diseñado con privacidad como requisito de arquitectura. Los participantes son anónimos por defecto; no se recopila información personal identificable.",
        },
        {
          question: "¿Klapzo funciona bien con conexiones lentas?",
          answer:
            "Sí. Klapzo corre en el edge (Cloudflare Workers), lo que significa latencia mínima. La app de participante es ligera y funciona en redes 3G.",
        },
      ],
    },
    finalCta: {
      headline: "Tu próxima sesión empieza aquí",
      body: "Únete a los facilitadores que ya eligieron una alternativa latinoamericana — en tu idioma, con precios locales.",
      cta: "Crear mi cuenta gratis",
    },
    footer: {
      made: "Hecho con cuidado en Brasil por The Labs Group · SkilLab",
      legal: `© ${new Date().getFullYear()} SkilLab. Todos los derechos reservados.`,
      privacy: "Privacidad",
      links: [
        { label: "Funciones", href: "#recursos" },
        { label: "Precios", href: "#precos" },
        { label: "Kit de Facilitación", href: "/kit" },
        { label: "Privacidad", href: "/privacidade" },
        { label: "Términos", href: "/termos" },
        { label: "Contacto", href: "mailto:contato@klapzo.app" },
      ],
    },
  },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

type FeatureCardProps = {
  title: string;
  description: string;
};

function FeatureCard({ title, description }: FeatureCardProps): React.ReactElement {
  return (
    <article className="bg-background border border-border rounded-md p-6 flex flex-col gap-3">
      <h3 className="font-display text-display-sm text-foreground">{title}</h3>
      <p className="font-body text-base text-foreground leading-relaxed">{description}</p>
    </article>
  );
}

type StepCardProps = {
  number: string;
  title: string;
  description: string;
};

function StepCard({ number, title, description }: StepCardProps): React.ReactElement {
  return (
    <div className="flex flex-col gap-4">
      <span
        className="font-display text-display-lg text-primary bg-foreground inline-flex items-center justify-center w-16 h-16 rounded-none"
        aria-hidden="true"
      >
        {number}
      </span>
      <h3 className="font-display text-display-sm text-foreground">{title}</h3>
      <p className="font-body text-base text-foreground leading-relaxed max-w-prose">{description}</p>
    </div>
  );
}

type FaqItemProps = {
  question: string;
  answer: string;
  index: number;
};

function FaqItem({ question, answer, index }: FaqItemProps): React.ReactElement {
  const [open, setOpen] = React.useState<boolean>(false);
  const id = `faq-${index}`;

  return (
    <div className="border-b border-border">
      <button
        className="w-full flex items-center justify-between py-5 text-left gap-4 group"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="font-body font-bold text-base text-foreground group-hover:text-accent transition-colors">
          {question}
        </span>
        <span
          className="font-display text-display-sm text-foreground shrink-0 select-none"
          aria-hidden="true"
        >
          {open ? "−" : "+"}
        </span>
      </button>
      <div
        id={id}
        role="region"
        hidden={!open}
        className="pb-5"
      >
        <p className="font-body text-base text-foreground leading-relaxed max-w-prose">{answer}</p>
      </div>
    </div>
  );
}

type ComparisonRowProps = {
  them: string;
  us: string;
};

function ComparisonRow({ them, us }: ComparisonRowProps): React.ReactElement {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border border-border rounded-md overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 bg-surface">
        <span className="font-display text-display-sm text-foreground shrink-0" aria-hidden="true">✕</span>
        <span className="font-body text-sm text-foreground">{them}</span>
      </div>
      <div className="flex items-center gap-3 px-5 py-4 bg-primary">
        <span className="font-display text-display-sm text-primary-foreground shrink-0" aria-hidden="true">✓</span>
        <span className="font-body text-sm text-primary-foreground font-bold">{us}</span>
      </div>
    </div>
  );
}

// ─── Decorative SVG swoosh (brand graphic element, behind content, flat color) ─

function HeroSwoosh(): React.ReactElement {
  return (
    <svg
      aria-hidden="true"
      className="absolute right-0 top-0 w-72 h-72 md:w-96 md:h-96 opacity-20 pointer-events-none select-none text-primary"
      viewBox="0 0 400 400"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M 380 20 C 280 60, 60 120, 20 380"
        stroke="currentColor"
        strokeWidth="60"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

// ─── Exported components ──────────────────────────────────────────────────────

export function MarketingHeader({
  lang,
  onLangChange,
}: {
  lang: Lang;
  onLangChange: (l: Lang) => void;
}): React.ReactElement {
  const c = COPY[lang].nav;

  const langOptions: ReadonlyArray<{ code: Lang; label: string }> = [
    { code: "pt-BR", label: "PT" },
    { code: "en-US", label: "EN" },
    { code: "es-LATAM", label: "ES" },
  ];

  return (
    <header
      className="sticky top-0 z-50 bg-background border-b border-border"
      role="banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        {/* Wordmark */}
        <Link
          to="/"
          className="font-display text-display-sm text-foreground tracking-tight shrink-0"
          aria-label="Klapzo — página inicial"
        >
          Klapzo
        </Link>

        {/* Nav links (hidden on mobile) */}
        <nav
          className="hidden md:flex items-center gap-6"
          aria-label="Menu principal"
        >
          <a
            href="#recursos"
            className="font-body text-sm text-foreground hover:text-accent transition-colors"
          >
            {c.recursos}
          </a>
          <a
            href="#precos"
            className="font-body text-sm text-foreground hover:text-accent transition-colors"
          >
            {c.precos}
          </a>
          <Link
            to="/kit"
            className="font-body text-sm text-foreground hover:text-accent transition-colors"
          >
            {c.kit}
          </Link>
          <Link
            to="/comparar"
            className="font-body text-sm text-foreground hover:text-accent transition-colors"
          >
            {c.comparar}
          </Link>
        </nav>

        {/* Right: lang switcher + CTA */}
        <div className="flex items-center gap-3">
          {/* Language switcher */}
          <div
            className="flex items-center gap-1 border border-border rounded-md overflow-hidden"
            role="group"
            aria-label="Selecionar idioma"
          >
            {langOptions.map((opt) => (
              <button
                key={opt.code}
                onClick={() => onLangChange(opt.code)}
                aria-pressed={lang === opt.code}
                className={[
                  "font-body text-xs px-2 py-1 transition-colors",
                  lang === opt.code
                    ? "bg-foreground text-background"
                    : "bg-background text-foreground hover:bg-surface",
                ].join(" ")}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Primary CTA */}
          <Link
            to="/entrar"
            className="font-body text-sm font-bold bg-primary text-primary-foreground px-4 py-2 rounded-md hover:opacity-90 transition-opacity whitespace-nowrap"
          >
            {c.entrar}
          </Link>
        </div>
      </div>
    </header>
  );
}

/**
 * The typed way into a room (W10a, US-LV-17 AC2).
 *
 * A phone that cannot scan — glare on the projector, a cracked lens, a camera permission nobody
 * wants to grant in front of forty colleagues — still has to get in, and until now this page had no
 * way to. The alphabet is checked HERE, with the same rule the API uses, so a code with a character
 * that cannot exist is a sentence on this page rather than a round-trip that ends on a blank screen.
 * A code that is well-formed but wrong is NOT this form's business: it navigates, and `/j/:code`
 * shows its own refusal, which is the one the participant will see again if they retype it.
 */
function JoinByCode({ copy }: { copy: HeroCopy["join"] }): React.ReactElement {
  const navigate = useNavigate();
  const [raw, setRaw] = React.useState("");
  const [invalid, setInvalid] = React.useState(false);

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    const code = normalizeJoinCode(raw);
    if (!code) {
      setInvalid(true);
      return;
    }
    navigate(`/j/${code}`);
  };

  return (
    <form onSubmit={submit} className="mt-10 flex flex-col gap-2">
      <label htmlFor="join-code" className="font-body text-sm font-bold text-foreground">
        {copy.label}
      </label>
      <div className="flex flex-wrap gap-3">
        <input
          id="join-code"
          value={raw}
          // Uppercased as it is typed, so what the person sees matches what is on the wall — the
          // normaliser would accept it either way.
          onChange={(e) => {
            setRaw(e.target.value.toUpperCase());
            setInvalid(false);
          }}
          placeholder={copy.placeholder}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          // Not `maxLength={6}`: people type "ABC-234" and " ABC 234 " off a projector, and a hard
          // cap would silently eat the last character before the normaliser ever sees the string.
          maxLength={12}
          aria-invalid={invalid}
          aria-describedby={invalid ? "join-code-error" : undefined}
          className="w-40 rounded-md border border-border bg-background px-4 py-3 font-display text-lg tracking-widest text-foreground"
        />
        <button
          type="submit"
          className="font-body text-base font-bold bg-primary text-primary-foreground px-8 py-3 rounded-md hover:opacity-90 transition-opacity"
        >
          {copy.cta}
        </button>
      </div>
      {invalid && (
        <p id="join-code-error" role="alert" className="font-body text-sm font-bold text-accent">
          {copy.invalid}
        </p>
      )}
    </form>
  );
}

export function Landing({ lang }: { lang: Lang }): React.ReactElement {
  const c = COPY[lang];

  return (
    <main id="main-content">
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section
        aria-labelledby="hero-headline"
        className="relative bg-background overflow-hidden"
      >
        <HeroSwoosh />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-36 lg:py-44">
          {/* Badge */}
          <div className="mb-6">
            <span className="inline-block font-body text-xs font-bold bg-primary text-primary-foreground px-3 py-1 rounded-full uppercase tracking-wide">
              {c.hero.badge}
            </span>
          </div>

          {/* Headline */}
          <h1
            id="hero-headline"
            className="font-display text-display-xl text-foreground max-w-3xl mb-6"
          >
            {c.hero.headline}
          </h1>

          {/* Subheadline */}
          <p className="font-body text-lg text-foreground max-w-prose leading-relaxed mb-10">
            {c.hero.subheadline}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <Link
              to="/entrar"
              className="font-body text-base font-bold bg-primary text-primary-foreground px-8 py-4 rounded-md hover:opacity-90 transition-opacity"
            >
              {c.hero.cta}
            </Link>
            <a
              href="#recursos"
              className="font-body text-base font-bold text-accent border border-accent px-8 py-4 rounded-md hover:bg-surface transition-colors"
            >
              {c.hero.ctaSecondary}
            </a>
          </div>

          <JoinByCode copy={c.hero.join} />

          {/* Used by SkilLab note */}
          <p className="font-body text-sm text-foreground mt-10 italic">
            {lang === "pt-BR"
              ? "Usado internamente pela SkilLab antes de ser lançado ao mercado."
              : lang === "en-US"
              ? "Used internally by SkilLab before market launch."
              : "Usado internamente por SkilLab antes del lanzamiento al mercado."}
          </p>
        </div>
      </section>

      {/* ── Value / substitution ──────────────────────────────────────────── */}
      <section
        id="recursos"
        aria-labelledby="value-headline"
        className="bg-surface"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="max-w-2xl mb-12">
            <h2
              id="value-headline"
              className="font-display text-display-lg text-foreground mb-4"
            >
              {c.value.headline}
            </h2>
            <p className="font-body text-lg text-foreground leading-relaxed max-w-prose">
              {c.value.body}
            </p>
          </div>

          <p className="font-body text-sm font-bold text-foreground uppercase tracking-widest mb-4">
            {c.value.comparisonLabel}
          </p>

          <div className="flex flex-col gap-3">
            {c.value.comparisonItems.map((item) => (
              <ComparisonRow key={item.them} them={item.them} us={item.us} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Klapzo Live features ──────────────────────────────────────────── */}
      <section
        aria-labelledby="live-headline"
        className="bg-background"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          {/* Section header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div className="max-w-xl">
              <div className="w-12 h-1.5 bg-secondary mb-4" aria-hidden="true" />
              <h2
                id="live-headline"
                className="font-display text-display-lg text-foreground mb-4"
              >
                {c.live.headline}
              </h2>
              <p className="font-body text-lg text-foreground leading-relaxed max-w-prose">
                {c.live.subhead}
              </p>
            </div>
            <p className="font-body text-sm text-foreground italic max-w-xs">
              {c.live.participantNote}
            </p>
          </div>

          {/* Feature cards — neutral palette, ONE accent (blue border on hover) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {c.live.formats.map((fmt) => (
              <FeatureCard
                key={fmt.title}
                title={fmt.title}
                description={fmt.description}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── Klapzo Kit features ───────────────────────────────────────────── */}
      <section
        aria-labelledby="kit-headline"
        className="bg-brand-black"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          {/* Section header — inverted surface */}
          <div className="mb-14">
            <div className="w-12 h-1.5 bg-primary mb-4" aria-hidden="true" />
            <h2
              id="kit-headline"
              className="font-display text-display-lg text-white mb-4"
            >
              {c.kit.headline}
            </h2>
            <p className="font-body text-lg text-white leading-relaxed max-w-prose opacity-80">
              {c.kit.subhead}
            </p>
          </div>

          {/* Feature list — on dark surface, yellow accent bar + white text */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
            {c.kit.features.map((feat) => (
              <div key={feat.title} className="flex flex-col gap-3">
                <div className="w-8 h-1 bg-primary" aria-hidden="true" />
                <h3 className="font-display text-display-sm text-white">{feat.title}</h3>
                <p className="font-body text-base text-white leading-relaxed max-w-prose opacity-80">
                  {feat.description}
                </p>
              </div>
            ))}
          </div>

          {/* Kit CTA */}
          <div className="mt-14">
            <Link
              to="/kit"
              className="inline-block font-body text-base font-bold bg-primary text-primary-foreground px-8 py-4 rounded-md hover:opacity-90 transition-opacity"
            >
              {lang === "pt-BR"
                ? "Explorar o Kit"
                : lang === "en-US"
                ? "Explore the Kit"
                : "Explorar el Kit"}
            </Link>
          </div>
        </div>
      </section>

      {/* ── Como funciona / How it works ─────────────────────────────────── */}
      <section
        aria-labelledby="how-headline"
        className="bg-primary"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <h2
            id="how-headline"
            className="font-display text-display-lg text-primary-foreground mb-14"
          >
            {c.howItWorks.headline}
          </h2>

          {/* Steps — on yellow surface: black text only, no blue accent */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
            {c.howItWorks.steps.map((step) => (
              <StepCard
                key={step.number}
                number={step.number}
                title={step.title}
                description={step.description}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── Preços / Pricing ──────────────────────────────────────────────── */}
      <section
        id="precos"
        aria-labelledby="pricing-headline"
        className="bg-background"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="max-w-xl mb-12">
            <h2
              id="pricing-headline"
              className="font-display text-display-lg text-foreground mb-4"
            >
              {c.pricing.headline}
            </h2>
            <p className="font-body text-lg text-foreground leading-relaxed max-w-prose">
              {c.pricing.subhead}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl">
            {c.pricing.plans.map((plan) => (
              <div
                key={plan.name}
                className={[
                  "flex flex-col gap-6 rounded-md p-8 border",
                  plan.highlight
                    ? "bg-foreground text-background border-foreground"
                    : "bg-surface text-foreground border-border",
                ].join(" ")}
              >
                {/* Plan name */}
                <div>
                  <h3
                    className={[
                      "font-display text-display-sm",
                      plan.highlight ? "text-primary" : "text-foreground",
                    ].join(" ")}
                  >
                    {plan.name}
                  </h3>
                  <p
                    className={[
                      "font-display text-display-md mt-1",
                      plan.highlight ? "text-white" : "text-foreground",
                    ].join(" ")}
                  >
                    {plan.price}
                  </p>
                  <p
                    className={[
                      "font-body text-sm mt-1",
                      plan.highlight ? "text-white opacity-70" : "text-foreground opacity-70",
                    ].join(" ")}
                  >
                    {plan.priceSub}
                  </p>
                </div>

                {/* Features */}
                <ul className="flex flex-col gap-2 flex-1">
                  {plan.features.map((feat) => (
                    <li key={feat} className="flex items-start gap-2">
                      <span
                        className={[
                          "font-display text-sm shrink-0 mt-0.5",
                          plan.highlight ? "text-primary" : "text-foreground",
                        ].join(" ")}
                        aria-hidden="true"
                      >
                        —
                      </span>
                      <span
                        className={[
                          "font-body text-sm",
                          plan.highlight ? "text-white" : "text-foreground",
                        ].join(" ")}
                      >
                        {feat}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                {plan.highlight ? (
                  <Link
                    to="/entrar"
                    className="font-body text-sm font-bold bg-primary text-primary-foreground px-6 py-3 rounded-md text-center hover:opacity-90 transition-opacity"
                  >
                    {plan.cta}
                  </Link>
                ) : (
                  <Link
                    to="/entrar"
                    className="font-body text-sm font-bold bg-background text-foreground border border-foreground px-6 py-3 rounded-md text-center hover:bg-surface transition-colors"
                  >
                    {plan.cta}
                  </Link>
                )}
              </div>
            ))}
          </div>

          <p className="font-body text-sm text-foreground opacity-60 mt-8 max-w-prose italic">
            {c.pricing.disclaimer}
          </p>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────────── */}
      <section
        aria-labelledby="faq-headline"
        className="bg-surface"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <h2
            id="faq-headline"
            className="font-display text-display-lg text-foreground mb-10"
          >
            {c.faq.headline}
          </h2>

          <div role="list">
            {c.faq.items.map((item, i) => (
              <div key={item.question} role="listitem">
                <FaqItem
                  question={item.question}
                  answer={item.answer}
                  index={i}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA band ────────────────────────────────────────────────── */}
      <section
        aria-labelledby="final-cta-headline"
        className="bg-brand-black"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 text-center">
          {/* Decorative accent bar */}
          <div className="w-16 h-1.5 bg-primary mx-auto mb-8" aria-hidden="true" />

          <h2
            id="final-cta-headline"
            className="font-display text-display-lg text-white max-w-2xl mx-auto mb-6"
          >
            {c.finalCta.headline}
          </h2>
          <p className="font-body text-lg text-white opacity-80 max-w-prose mx-auto mb-10 leading-relaxed">
            {c.finalCta.body}
          </p>
          <Link
            to="/entrar"
            className="inline-block font-body text-base font-bold bg-primary text-primary-foreground px-10 py-5 rounded-md hover:opacity-90 transition-opacity"
          >
            {c.finalCta.cta}
          </Link>
        </div>
      </section>
    </main>
  );
}

export function MarketingFooter(): React.ReactElement {
  // Use pt-BR for footer — it's structural, not content
  const c = COPY["pt-BR"].footer;

  return (
    <footer
      className="bg-background border-t border-border"
      role="contentinfo"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          {/* Wordmark + tagline */}
          <div className="flex flex-col gap-1">
            <span className="font-display text-display-sm text-foreground">Klapzo</span>
            <span className="font-body text-xs text-foreground opacity-60">{c.made}</span>
          </div>

          {/* Links */}
          <nav aria-label="Links do rodapé" className="flex flex-wrap gap-x-6 gap-y-2">
            {c.links.map((link) =>
              link.href.startsWith("/") ? (
                <Link
                  key={link.label}
                  to={link.href}
                  className="font-body text-sm text-foreground hover:text-accent transition-colors"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  className="font-body text-sm text-foreground hover:text-accent transition-colors"
                >
                  {link.label}
                </a>
              )
            )}
          </nav>
        </div>

        {/* Copyright */}
        <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="font-body text-xs text-foreground opacity-60">{c.legal}</p>
          <a
            href="/privacidade"
            className="font-body text-xs text-foreground hover:text-accent transition-colors opacity-60 hover:opacity-100"
          >
            {c.privacy}
          </a>
        </div>
      </div>
    </footer>
  );
}
